import {
  autoUpdate,
  FloatingNode,
  FloatingPortal,
  flip,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { CaretDownIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/ui/icon'
import { Label } from '@/components/ui/label'
import { Typography } from '@/components/ui/typography'
import { useDebounce } from '@/hooks'

import * as S from './link-color-select.styled'
import {
  CUSTOM_COLOR_APPLY_DELAY,
  DEFAULT_CUSTOM_COLOR,
  DEFAULT_LINK_COLOR,
  LINK_COLORS,
} from './linked-columns.constants'
import { useSavedLinkColors } from './use-saved-link-colors'

interface LinkColorSelectProps {
  disabled?: boolean
  value: string
  onChange: (value: string) => void
}

export const LinkColorSelect = ({ disabled = false, value, onChange }: LinkColorSelectProps) => {
  const { t } = useTranslation('common')
  const [isOpen, setIsOpen] = useState(false)

  // Note: the native picker fires on every drag step, and applying each one re-renders the whole page,
  // so the custom color is a draft in the swatch and is applied once the picking pauses; the timer
  // outlives the popover, so closing it right after picking still applies the color
  const [draft, setDraft] = useState<string | null>(null)
  const { debounce, reset } = useDebounce({
    delay: CUSTOM_COLOR_APPLY_DELAY,
    unmountCleanup: false,
  })

  const [savedColors, saveColor] = useSavedLinkColors()

  const handleCustomChange = (color: string) => {
    setDraft(color)
    debounce(() => {
      onChange(color)
      if (!LINK_COLORS.includes(color)) saveColor(color)
      setDraft(null)
    })
  }

  const nodeId = useFloatingNodeId()

  const { refs, floatingStyles, context } = useFloating({
    middleware: [offset(4), flip()],
    nodeId,
    onOpenChange: setIsOpen,
    open: isOpen && !disabled,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context),
    useRole(context, { role: 'dialog' }),
  ])

  const isCustom = value !== DEFAULT_LINK_COLOR && !LINK_COLORS.includes(value)

  const customColor = draft ?? (isCustom ? value : null)

  const handleSelect = (color: string) => {
    reset()
    setDraft(null)
    onChange(color)
    setIsOpen(false)
  }

  return (
    <S.Wrapper>
      <Label disabled={disabled} size="small">
        {t('linkedColumns.linkColor')}
      </Label>
      <S.Trigger
        ref={refs.setReference}
        $open={isOpen}
        aria-label={t('linkedColumns.linkColor')}
        disabled={disabled}
        type="button"
        {...getReferenceProps()}
      >
        <S.Swatch
          $color={value}
          $default={value === DEFAULT_LINK_COLOR}
          $selected={false}
          as="span"
        />
        <Icon icon={CaretDownIcon} size="1.4rem" />
      </S.Trigger>

      <FloatingNode id={nodeId}>
        {isOpen && !disabled && (
          <FloatingPortal>
            <S.Popover ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
              <S.Grid role="radiogroup">
                <S.Swatch
                  $default
                  $color={DEFAULT_LINK_COLOR}
                  $selected={value === DEFAULT_LINK_COLOR}
                  aria-checked={value === DEFAULT_LINK_COLOR}
                  aria-label={t('linkedColumns.defaultColor')}
                  role="radio"
                  title={t('linkedColumns.defaultColor')}
                  type="button"
                  onClick={() => handleSelect(DEFAULT_LINK_COLOR)}
                />
                {LINK_COLORS.map((color) => (
                  <S.Swatch
                    key={color}
                    $color={color}
                    $default={false}
                    $selected={value === color}
                    aria-checked={value === color}
                    aria-label={color}
                    role="radio"
                    type="button"
                    onClick={() => handleSelect(color)}
                  />
                ))}
              </S.Grid>

              <S.CustomSection>
                <Label size="small">{t('linkedColumns.customColor')}</Label>
                <S.CustomRow>
                  <S.CustomSwatch
                    $color={customColor ?? DEFAULT_LINK_COLOR}
                    $selected={customColor !== null}
                  >
                    <S.ColorInput
                      aria-label={t('linkedColumns.customColor')}
                      type="color"
                      value={customColor ?? DEFAULT_CUSTOM_COLOR}
                      onChange={(event) => handleCustomChange(event.target.value)}
                    />
                  </S.CustomSwatch>
                  {savedColors
                    .filter((color) => color !== value)
                    .map((color) => (
                      <S.Swatch
                        key={color}
                        $color={color}
                        $default={false}
                        $selected={value === color}
                        aria-checked={value === color}
                        aria-label={color}
                        role="radio"
                        title={color}
                        type="button"
                        onClick={() => handleSelect(color)}
                      />
                    ))}
                  {customColor && (
                    <Typography as="span" color="black500" size="text-small">
                      {customColor}
                    </Typography>
                  )}
                </S.CustomRow>
              </S.CustomSection>
            </S.Popover>
          </FloatingPortal>
        )}
      </FloatingNode>
    </S.Wrapper>
  )
}
